import EverTrue from 'app';
import Api from 'entities/helpers/api';
import { withTransientRetry } from 'entities/helpers/transient-retry';

/**
 * Execute a GraphQL query/mutation against the cadences GraphQL endpoint
 * via the et-gateway (Cosmo Router) at gateway/graphql.
 *
 * Auth and impersonation headers are handled automatically by the Api/Requester layer.
 * The Cosmo Router requires ET-Organization-ID as a header (not just the oid query param
 * that the Requester adds by default), so we pass it explicitly.
 *
 * @param {string} query - GraphQL query or mutation string
 * @param {Object} variables - Variables for the query
 * @returns {Promise<Object>} The data portion of the GraphQL response
 * @throws {Error} If the response contains GraphQL errors or the request fails
 */
const cadenceGqlCall = async (query, variables = {}) => {
  const orgId = EverTrue.store.org?.get('id');
  if (!orgId) {
    throw new Error('Organization context not available. Please refresh the page.');
  }

  const isMutation = /^\s*mutation\b/.test(query);
  const res = await withTransientRetry(
    () =>
      Api.GATEWAY.GRAPHQL.post({
        data: JSON.stringify({ query, variables }),
        headers: {
          'ET-Organization-ID': String(orgId),
        },
      }),
    { enabled: !isMutation, label: 'cadence-graphql' }
  );

  if (res?.errors?.length) {
    const operationMatch = query.match(/^\s*(query|mutation)\s+(\w+)/);
    const operationName = operationMatch ? operationMatch[2] : 'unknown';
    if (process.env.NODE_ENV === 'development') {
      console.error(`[cadenceGqlCall] ${operationName} failed:`, JSON.stringify(res.errors, null, 2));
    } else {
      console.error(`[cadenceGqlCall] ${operationName} failed`);
    }

    // Cosmo Router wraps subgraph errors: the real code + message are nested under extensions.errors[0].
    // Do not read the top-level message ("Failed to fetch from Subgraph '...'") — it is always generic.
    const nestedError = res.errors[0]?.extensions?.errors?.[0];
    let message = nestedError?.message || res.errors[0].message || 'GraphQL error';

    // Strip any remaining router/downstream prefixes
    message = message
      .replace(/^Error:\s*/i, '')
      .replace(/^Failed to fetch from Subgraph '[^']*'[.:]\s*/i, '')
      .replace(/^DOWNSTREAM_SERVICE_ERROR[.:]\s*/i, '');

    // Detect known backend validation patterns before the length/safety check
    // so callers get actionable messages instead of the generic fallback.
    if (message.includes('html_body') || message.includes('text_body'))
      message = 'One or more email steps are missing a body. Go back and add content to each email step.';
    else if (message.includes('EmailEventConfig') || message.includes('validation error for Email'))
      message =
        'One or more email steps have incomplete configuration. Go back and fill in the subject and body for each email step.';

    // Extract error code: nested subgraph location first, top-level as fallback.
    const code = nestedError?.extensions?.code ?? res.errors[0]?.extensions?.code;

    // Only forward messages that are short and contain no internal identifiers.
    // Internal errors (stack traces, DB field names, numeric IDs, UUIDs) are
    // replaced with a generic message to prevent information leakage.
    // Exception: a small set of codes whose messages are explicitly authored for user
    // display may be longer than 150 chars. Only list codes confirmed to carry a clean,
    // user-facing message through the Cosmo gateway (ATTACHMENT_TOO_LARGE, CADENCE_ACTIVE).
    // Do NOT widen this to all non-DOWNSTREAM codes — VALIDATION_ERROR, for instance, can
    // contain Pydantic field paths and class names that are not caught by the filters below.
    const bypassesLengthCap = code === 'ATTACHMENT_TOO_LARGE' || code === 'CADENCE_ACTIVE';
    const isSafe =
      (bypassesLengthCap || message.length <= 150) &&
      !/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(message) && // UUID
      !/\b\d{7,}\b/.test(message) && // long numeric IDs (7+ digits — counts/limits are safe, DB PKs are not)
      !/\b\w+_(id|at|by|key)\b/.test(message) && // snake_case internal field names
      !/\b(OperationalError|pymysql|IntegrityError|DatabaseError|sqlalchemy|traceback|TypeError|ValueError|KeyError|AttributeError)\b/i.test(
        message
      ) &&
      !/\b\d+ validation error(s)? for [A-Z]/.test(message); // Pydantic traceback header

    const err = new Error(isSafe ? message : 'Something went wrong. Please try again or contact support.');
    // Attach the machine-readable error code so callers can branch on it without parsing
    // message strings. Skip DOWNSTREAM_SERVICE_ERROR — that is the gateway's generic wrapper
    // code for an uncoded backend error, not a real application-level code.
    if (code && code !== 'DOWNSTREAM_SERVICE_ERROR') err.code = code;
    throw err;
  }

  return res?.data;
};

export default cadenceGqlCall;
